/**
 * Hooks local-first de MEDIA (F3-F): activo de vídeo, biblioteca por curso y
 * progreso de visionado. Mismo cableado fino que use-course-modules (PowerSync
 * + TanStack Query, offline-first).
 */
import type { Accessor } from "solid-js";
import {
  getAllWatchProgressQuery,
  getCourseVideosQuery,
  getVideoAssetQuery,
  getWatchProgressQuery,
  mediaQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type { VideoAsset, VideoWatchProgress } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Un `video_asset` por id (metadatos sincronizados; sin storage_key). */
export function useVideoAsset(videoId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<VideoAsset>({
    queryKey: () => mediaQueryKeys.asset(videoId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = videoId();
      return db && id ? getVideoAssetQuery(db, id) : null;
    },
    enabled: () => Boolean(videoId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Biblioteca de vídeos de un curso (autor / picker). */
export function useCourseVideos(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<VideoAsset>({
    queryKey: () => mediaQueryKeys.byCourse(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseVideosQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Progreso de visionado del usuario actual para un vídeo. */
export function useWatchProgress(videoId: Accessor<string | undefined>) {
  const base = useBase();
  const { userId } = useSession();
  return createPowerSyncSingle<VideoWatchProgress>({
    queryKey: () => mediaQueryKeys.watch(videoId(), userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = videoId();
      const uid = userId();
      return db && id && uid ? getWatchProgressQuery(db, uid, id) : null;
    },
    enabled: () => Boolean(videoId()) && Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Todo el progreso de visionado del usuario (badges "continuar en…"). */
export function useAllWatchProgress() {
  const base = useBase();
  const { userId } = useSession();
  return createPowerSyncQuery<VideoWatchProgress>({
    queryKey: () => mediaQueryKeys.watchAll(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const uid = userId();
      return db && uid ? getAllWatchProgressQuery(db, uid) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
